import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { getUrl } from 'aws-amplify/storage';
import awsConfig from '../../config/aws-config';
import classes from "./MusicProjectModal.module.css";
// import { use } from "react";

export const MusicProjectModal = ({ open, onClose, project, creative, projectsArray, fromMemberPage }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragTime, setDragTime] = useState(0);
  const [isTouching, setIsTouching] = useState(false);
  const [horizontalOffset, setHorizontalOffset] = useState(0);
  const audioRef = useRef(null);
  const progressBarRef = useRef(null);
  const progressBarElementRefPhone = useRef(null);
  const progressBarElementRefDesktop = useRef(null);
  const touchStartY = useRef(null);

  // Get the active progress bar element ref (phone or desktop)
  const getActiveProgressBarRef = () => {
    // Check which one is visible/active
    if (progressBarElementRefPhone.current && progressBarElementRefPhone.current.offsetParent !== null) {
      return progressBarElementRefPhone.current;
    }
    if (progressBarElementRefDesktop.current && progressBarElementRefDesktop.current.offsetParent !== null) {
      return progressBarElementRefDesktop.current;
    }
    // Fallback to phone (mobile first)
    return progressBarElementRefPhone.current || progressBarElementRefDesktop.current;
  };

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    const syncHorizontalOffset = () => {
      setHorizontalOffset(window.scrollX || window.pageXOffset || 0);
    };

    syncHorizontalOffset();
    window.addEventListener("scroll", syncHorizontalOffset, { passive: true });
    window.addEventListener("resize", syncHorizontalOffset);

    return () => {
      window.removeEventListener("scroll", syncHorizontalOffset);
      window.removeEventListener("resize", syncHorizontalOffset);
    };
  }, []);

  useEffect(() => {
    if (project && projectsArray) {
      const index = projectsArray.findIndex((p) => p.id === project.id);
      if (index !== -1) {
        setCurrentIndex(index);
      }
    }
  }, [project, projectsArray]);

  // Resolve audio URL asynchronously when project changes
  useEffect(() => {
    const resolveAudioUrl = async () => {
      if (!open) {
        setAudioUrl(null);
        return;
      }

      const currentProject = projectsArray?.[currentIndex] || project;
      const audioData = currentProject?.audios?.[0];
      
      if (!audioData) {
        setAudioUrl(null);
        return;
      }

      // Use audioKey if available (from useCreative processing), otherwise use url
      const audioKey = audioData.audioKey || (typeof audioData === 'string' ? audioData : audioData.url);
      
      if (!audioKey) {
        setAudioUrl(null);
        return;
      }

      try {
        // Normalize the key - remove any duplicate 'public/' prefixes
        let cleanKey = audioKey;
        while (cleanKey.startsWith('public/public/')) {
          cleanKey = cleanKey.replace('public/public/', 'public/');
        }
        
        // IMPORTANT: getUrl with accessLevel: 'public' automatically adds 'public/' prefix
        // So we need to REMOVE the prefix if it exists before passing to getUrl
        let keyForGetUrl = cleanKey;
        if (keyForGetUrl.startsWith('public/')) {
          keyForGetUrl = keyForGetUrl.replace('public/', '');
        }
        
        // Use getUrl from AWS Amplify to get the proper URL
        // This handles authentication and returns either a CloudFront URL (if configured for media)
        // or a signed S3 URL that will work regardless of bucket permissions
        const result = await getUrl({
          key: keyForGetUrl,
          options: {
            accessLevel: 'public',
            validateObjectExistence: false
          }
        });
        
        setAudioUrl(result.url.toString());
      } catch (error) {
        try {
          let cleanKey = audioKey;
          while (cleanKey.startsWith('public/public/')) {
            cleanKey = cleanKey.replace('public/public/', 'public/');
          }
          if (!cleanKey.startsWith('public/')) {
            cleanKey = `public/${cleanKey}`;
          }
          const fallbackUrl = `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
          setAudioUrl(fallbackUrl);
        } catch (fallbackError) {
          setAudioUrl(audioKey);
        }
      }
    };

    resolveAudioUrl();
  }, [open, project, currentIndex, projectsArray]);

  useEffect(() => {
    if (open && audioRef.current && audioUrl) {
      const audio = audioRef.current;
      
      // Reset audio state when URL changes
      audio.pause();
      audio.currentTime = 0;
      setCurrentTime(0);
      setIsPlaying(false);
      setIsDragging(false);
      setIsTouching(false);
      setDragTime(0);
      touchStartY.current = null;
      
      // Set new source and load
      audio.src = audioUrl;
      audio.load();
      
      const updateTime = () => setCurrentTime(audio.currentTime);
      const updateDuration = () => setDuration(audio.duration);
      const handleEnded = () => {
        setIsPlaying(false);
        if (projectsArray && projectsArray.length > 1) {
          handleNext();
        }
      };
      const handleError = (e) => {
        setIsPlaying(false);
      };
      const handleCanPlay = () => {
      };

      audio.addEventListener("timeupdate", updateTime);
      audio.addEventListener("loadedmetadata", updateDuration);
      audio.addEventListener("ended", handleEnded);
      audio.addEventListener("error", handleError);
      audio.addEventListener("canplay", handleCanPlay);

      return () => {
        audio.removeEventListener("timeupdate", updateTime);
        audio.removeEventListener("loadedmetadata", updateDuration);
        audio.removeEventListener("ended", handleEnded);
        audio.removeEventListener("error", handleError);
        audio.removeEventListener("canplay", handleCanPlay);
      };
    }
  }, [open, currentIndex, project, projectsArray, audioUrl]);

  useEffect(() => {
    if (audioRef.current && audioUrl) {
      const audio = audioRef.current;
      
      if (isPlaying) {
        // Check if audio is ready to play
        if (audio.readyState >= 2) { // HAVE_CURRENT_DATA or higher
          const playPromise = audio.play();
          if (playPromise !== undefined) {
            playPromise
              .then(() => {
              })
              .catch((err) => {
                setIsPlaying(false);
              });
          }
        } else {
          // Wait for audio to be ready
          const handleCanPlayThrough = () => {
            audio.removeEventListener("canplaythrough", handleCanPlayThrough);
            const playPromise = audio.play();
            if (playPromise !== undefined) {
              playPromise
                .then(() => {
                })
                .catch((err) => {
                  setIsPlaying(false);
                });
            }
          };
          audio.addEventListener("canplaythrough", handleCanPlayThrough);
          // Also try to load if not already loading
          if (audio.networkState === 0) { // NETWORK_EMPTY
            audio.load();
          }
        }
      } else {
        audio.pause();
      }
    }
  }, [isPlaying, audioUrl]);

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handlePrevious = () => {
    if (projectsArray && projectsArray.length > 0) {
      const newIndex = currentIndex === 0 ? projectsArray.length - 1 : currentIndex - 1;
      setCurrentIndex(newIndex);
      setCurrentTime(0);
      if (isPlaying) {
        setTimeout(() => setIsPlaying(true), 100);
      }
    }
  };

  const handleNext = () => {
    if (projectsArray && projectsArray.length > 0) {
      const newIndex = (currentIndex + 1) % projectsArray.length;
      setCurrentIndex(newIndex);
      setCurrentTime(0);
      if (isPlaying) {
        setTimeout(() => setIsPlaying(true), 100);
      }
    }
  };

  // Simplified progress calculation - always uses progress bar element directly
  const calculateProgressFromX = (clientX) => {
    const barElement = getActiveProgressBarRef();
    if (!barElement || !duration) return null;
    
    const rect = barElement.getBoundingClientRect();
    
    // Calculate relative position within the progress bar
    const relativeX = clientX - rect.left;
    const percent = Math.max(0, Math.min(1, relativeX / rect.width));
    const newTime = percent * duration;
    
    return { percent, newTime };
  };

  const handleProgressMouseDown = (e) => {
    if (!duration) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const result = calculateProgressFromX(e.clientX);
    if (!result) return;
    
    setIsDragging(true);
    setDragTime(result.newTime);
    
    // Update audio immediately
    if (audioRef.current) {
      audioRef.current.currentTime = result.newTime;
      setCurrentTime(result.newTime);
    }
  };

  // Completely rewritten touch handler - simpler and more direct
  const handleProgressTouchStart = (e) => {
    if (!duration) {
      return;
    }
    
    const barElement = getActiveProgressBarRef();
    if (!barElement) return;
    
    // Stop all default behaviors immediately
    e.preventDefault();
    e.stopPropagation();
    
    const touch = e.touches[0];
    if (!touch) return;
    
    // Store initial touch position for scroll detection
    touchStartY.current = touch.clientY;
    
    // Calculate position using the progress bar element
    const result = calculateProgressFromX(touch.clientX);
    if (!result) return;
    
    // Set dragging state
    setIsTouching(true);
    setIsDragging(true);
    setDragTime(result.newTime);
    
    // Update audio position immediately
    if (audioRef.current) {
      audioRef.current.currentTime = result.newTime;
      setCurrentTime(result.newTime);
    }
  };

  // Simplified global event handlers - cleaner approach
  useEffect(() => {
    if (!isDragging && !isTouching) return;

    const handleMouseMove = (e) => {
      const result = calculateProgressFromX(e.clientX);
      if (result) {
        setDragTime(result.newTime);
      }
    };

    const handleMouseUp = () => {
      if (audioRef.current && dragTime !== undefined && dragTime !== null) {
        audioRef.current.currentTime = dragTime;
        setCurrentTime(dragTime);
      }
      setIsDragging(false);
      setIsTouching(false);
    };

    const handleTouchMove = (e) => {
      if (!duration) return;
      
      const barElement = getActiveProgressBarRef();
      if (!barElement) return;
      
      const touch = e.touches[0];
      if (!touch) return;
      
      // Check if user is scrolling vertically vs seeking horizontally
      if (touchStartY.current !== null) {
        const deltaY = Math.abs(touch.clientY - touchStartY.current);
        // If significant vertical movement, cancel seeking and allow scroll
        if (deltaY > 15) {
          setIsDragging(false);
          setIsTouching(false);
          touchStartY.current = null;
          return;
        }
      }
      
      // Calculate new position
      const result = calculateProgressFromX(touch.clientX);
      if (result) {
        setDragTime(result.newTime);
      }
      
      // Always prevent default when seeking
      e.preventDefault();
      e.stopPropagation();
    };

    const handleTouchEnd = () => {
      if (audioRef.current && dragTime !== undefined && dragTime !== null) {
        audioRef.current.currentTime = dragTime;
        setCurrentTime(dragTime);
      }
      setIsDragging(false);
      setIsTouching(false);
      touchStartY.current = null;
    };

    const handleTouchCancel = () => {
      setIsDragging(false);
      setIsTouching(false);
      touchStartY.current = null;
    };

    // Handle touch events
    if (isTouching) {
      // Prevent body scroll
      const originalOverflow = document.body.style.overflow;
      const originalTouchAction = document.body.style.touchAction;
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
      
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd, { passive: false });
      document.addEventListener('touchcancel', handleTouchCancel, { passive: false });
      
      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.touchAction = originalTouchAction;
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
        document.removeEventListener('touchcancel', handleTouchCancel);
      };
    } 
    // Handle mouse events
    else {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isTouching, dragTime, duration]);

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const formatRemainingTime = (current, total) => {
    const remaining = total - current;
    if (!remaining || isNaN(remaining)) return "-0:00";
    const mins = Math.floor(remaining / 60);
    const secs = Math.floor(remaining % 60);
    return `-${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleClose = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    setIsDragging(false);
    setIsTouching(false);
    setDragTime(0);
    touchStartY.current = null;
    onClose();
  };

  if (!open || !project) return null;

  const currentProject = projectsArray?.[currentIndex] || project;
  const audioData = currentProject?.audios?.[0];
  // Use processed cover image if available, otherwise fall back to other sources
  const coverImage = audioData?.processedCoverImage || audioData?.coverImage || currentProject?.images?.[0]?.url || currentProject?.processedImages?.[0]?.processedUrl;
  // const album = audioData?.album || '';
  // const durationMs = audioData?.durationMs || null;
  
  // Use dragTime when dragging or touching, otherwise use currentTime
  const displayTime = (isDragging || isTouching) ? dragTime : currentTime;
  const progressPercent = duration ? (displayTime / duration) * 100 : 0;
  const studio = currentProject?.studio;
  const fixedPhoneFooterStyle = {
    transform: `translateX(-${horizontalOffset}px)`,
    minWidth: "335px",
    width: "max(100vw, 335px)"
  };

  return createPortal(
    <>
      <div className={classes.backdrop} onClick={handleBackdropClick} />
      <div className={classes.modal} onClick={handleBackdropClick}>
        <div className={classes.modalWrapper}>
          <div className={classes.modalContent} onClick={(e) => e.stopPropagation()}>
            <button className={classes.backButton} onClick={handleClose}>
              <svg className={classes.backButtonIcon} xmlns="http://www.w3.org/2000/svg" width="11" height="19" viewBox="0 0 11 19" fill="none">
                <path fillRule="evenodd" clipRule="evenodd" d="M0 8.99855L8.99855 0L10.2006 1.20208L2.35773 9.04498L10.2486 17.5934L8.99941 18.7465L0.0172915 9.01584L0 8.99855Z" fill="var(--black)"/>
              </svg>

              <svg className={classes.backButtonIconPhone} xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="var(--black)"/>
              </svg>
            </button>

            <button className={classes.closeButton} onClick={handleClose}>
              <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="var(--black)"/>
              </svg>
            </button>

            <div className={classes.modalBody}>
              <div className={classes.headerContainer}>
                {coverImage && (
                  <div className={classes.albumArtContainer}>
                    <img src={coverImage} alt={currentProject.title} className={classes.albumArt} />
                  </div>
                )}

                <div className={classes.songTitleContainer}>
                  <h2 className="titleSemibold2">{currentProject?.title}</h2>
                  <span style={{color: "var(--dark-grey-3)"}} className="regularMiddle">
                    {currentProject?.audios?.[0]?.artists?.join(', ')}
                  </span>
                </div>
              </div>

              <div className={classes.playerContainerPhone}>
                {audioUrl && (
                  <>
                    <div 
                      ref={progressBarRef}
                      className={`${classes.progressBarContainer} ${(isDragging || isTouching) ? classes.progressBarDragging : ''}`}
                      onMouseDown={handleProgressMouseDown}
                      onTouchStart={handleProgressTouchStart}
                      onTouchCancel={() => {
                        setIsDragging(false);
                        setIsTouching(false);
                        touchStartY.current = null;
                      }}
                    >
                      <div ref={progressBarElementRefPhone} className={classes.progressBar}>
                        <div 
                          className={classes.progressFill} 
                          style={{ width: `${progressPercent}%` }}
                        >
                          <div className={classes.progressHandle} />
                        </div>
                      </div>
                    </div>

                    <div className={classes.timeContainer}>
                      <span className="slimRegularMiniText">{formatTime(displayTime)}</span>
                      <span className="slimRegularMiniText">{formatRemainingTime(displayTime, duration)}</span>
                    </div>

                    <div className={classes.controlsContainer}>
                      {projectsArray && projectsArray.length > 1 && (
                        <button className={classes.controlButton} onClick={handlePrevious}>
                          <svg fill="var(--dark-grey)" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                            <path d="M6 6L6 18M18 6L10 12L18 18L18 6Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      )}
                      
                      <button className={classes.playPauseButton} onClick={handlePlayPause}>
                        {isPlaying ? (
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <rect x="6" y="4" width="4" height="16" fill="currentColor"/>
                            <rect x="14" y="4" width="4" height="16" fill="currentColor"/>
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M8 5V19L19 12L8 5Z" fill="currentColor"/>
                          </svg>
                        )}
                      </button>

                      {projectsArray && projectsArray.length > 1 && (
                        <button className={classes.controlButton} onClick={handleNext}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path fill="var(--dark-grey)" d="M18 6L18 18M6 6L14 12L6 18L6 6Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className={classes.detailsContainer}>
              {currentProject.description && (
                <div className={classes.descriptionSection}>
                  <h3 className="semiboldLarge">Projektbeschreibung</h3>
                  <p className="regularLarge">{currentProject.description}</p>
                </div>
              )}

              {studio && (
                <Link 
                  to={`/studio/${studio.id}`} 
                  className={classes.relatedCard}
                  onClick={handleClose}
                >
                  {studio.processedImages?.[0] && (
                    <div className={classes.relatedImage}>
                      <img src={studio.processedImages[0]} alt={studio.StudioName} />
                    </div>
                  )}
                  <div className={classes.relatedInfo}>
                    <p className="mediumLarge">{studio.StudioName}</p>
                    {studio.city && (
                      <p style={{color: "var(--dark-grey-3)"}} className="slimRegularSmallText">
                        {studio.city}{studio.country ? `, ${studio.country}` : ''}
                      </p>
                    )}
                  </div>
                </Link>
              )}
            </div>

            {audioUrl && (
              <>
                <div className={classes.playerContainer}>
                  <div 
                    ref={progressBarRef}
                    className={`${classes.progressBarContainer} ${(isDragging || isTouching) ? classes.progressBarDragging : ''}`}
                    onMouseDown={handleProgressMouseDown}
                    onTouchStart={handleProgressTouchStart}
                    onTouchCancel={() => {
                      setIsDragging(false);
                      setIsTouching(false);
                      touchStartY.current = null;
                    }}
                  >
                    <div ref={progressBarElementRefDesktop} className={classes.progressBar}>
                      <div 
                        className={classes.progressFill} 
                        style={{ width: `${progressPercent}%` }}
                      >
                        <div className={classes.progressHandle} />
                      </div>
                    </div>
                  </div>

                  <div className={classes.timeContainer}>
                    <span className="slimRegularMiniText">{formatTime(displayTime)}</span>
                    <span className="slimRegularMiniText">{formatRemainingTime(displayTime, duration)}</span>
                  </div>

                  <div className={classes.controlsContainer}>
                    {projectsArray && projectsArray.length > 1 && (
                      <button className={classes.controlButton} onClick={handlePrevious}>
                        <svg fill="var(--dark-grey)" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                          <path d="M6 6L6 18M18 6L10 12L18 18L18 6Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    )}
                    
                    <button className={classes.playPauseButton} onClick={handlePlayPause}>
                      {isPlaying ? (
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <rect x="6" y="4" width="4" height="16" fill="currentColor"/>
                          <rect x="14" y="4" width="4" height="16" fill="currentColor"/>
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M8 5V19L19 12L8 5Z" fill="currentColor"/>
                        </svg>
                      )}
                    </button>

                    {projectsArray && projectsArray.length > 1 && (
                      <button className={classes.controlButton} onClick={handleNext}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path fill="var(--dark-grey)" d="M18 6L18 18M6 6L14 12L6 18L6 6Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    )}
                  </div>
                </div>

                <audio 
                  ref={audioRef} 
                  preload="metadata" 
                  crossOrigin="anonymous"
                />
              </>
            )}

            {creative && !fromMemberPage && (
                <Link 
                  to={`/creative-editing/${creative.id}/project/${currentProject.id}`}
                  className={`${classes.editButton} mediumLarge`}
                >
                  Bearbeiten
                </Link>
            )}

            <div className={classes.phoneFooterContainer} style={fixedPhoneFooterStyle}>
              <Link to={`/creative-editing/${creative.id}/project/${currentProject.id}`} className={classes.phoneFooterButton} onClick={handleClose}>
                <span className="mediumLarge">Bearbeiten</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.getElementById("modal") || document.body
  );
};

export default MusicProjectModal;

